// KPI çözülmüş hücre / ızgara şemaları — schemas/kpi.ts 600 satır bölmesi (TODO-KPI-114/-144, salt taşıma); `kpi.ts` barrel'i yeniden dışa açar.
import { z } from "zod";
import {
  KPI_REPORT_TABLE_LAYOUTS,
  KPI_UPDATED_BY_KINDS,
  KPI_VALUE_STATUSES,
  KPI_WORKFLOW_STATES,
  KpiGridEntityScopeSchema,
  type KpiGridEntityScope,
  type KpiReportTableLayout,
} from "./kpi-constants.js";
import { KpiValueSourceSchema } from "./kpi-metrics.js";
import {
  ClientEntitySchema,
  KpiSetIndicatorSchema,
  type ClientEntity,
  type KpiSetIndicator,
} from "./kpi-sets.js";
import { KpiBreakdownCheckSchema } from "./kpi-values.js";

export const KpiResolvedCellSchema = z.object({
  /** `calc_error` = türetilmiş HESAPLANAMADI (payda sıfır / girdi eksik). */
  status: z.enum([...KPI_VALUE_STATUSES, "calc_error"]),
  value: z.number().optional(),
  text: z.string().optional(),
  isDerived: z.boolean(),
  /** Türetilmiş hücre girdilerinden en az biri XXX. */
  hasPending: z.boolean().optional(),
  missingRefs: z.array(z.string()).optional(),
  /**
   * TODO-KPI-111 — `breakdown_member_missing`'de eksik üye kodu → katalog etiketi ("Erkek (sette
   * yok)"); `missingRefs` bu kodları taşır. Yalnız doluyken yazılır; portalda düşer.
   */
  missingMemberLabels: z.record(z.string()).optional(),
  /**
   * K8 (Finar 2026-10-28) — ölçülmeyen kalemler (`partialRefs`: formülde atlanan gösterge kodları)
   * ya da işletmeler (`partialEntityIds`: konsolide/ara toplama girmeyen yaprak işletme kimlikleri)
   * toplamda atlandı. Yalnız doluyken yazılır (boş dizi yazılmaz). Kısmi kararı `isPartialKpiCell`.
   */
  partialRefs: z.array(z.string()).optional(),
  partialEntityIds: z.array(z.string()).optional(),
  /** K8 — hesabın tüm girdileri ölçülmüyor (calc_error nedeni); yalnız true iken yazılır. */
  allUnmeasured: z.boolean().optional(),
  /**
   * TODO-KPI-91 — calc_error'ın bilinen yapısal nedeni (motor `FormulaResult.failure`); yalnız
   * doluyken yazılır. Bugün yalnız AGIRLIKLI_ORT yazar: `wavg_count_mismatch` (değer/ağırlık alt
   * kalem sayıları eşit değil) ve `empty_group` (iki kategoride de alt kalem yok). `zero_denominator`
   * `FormulaFailure` tipinde ayrılmış ama bugün yazılmıyor.
   * Kırılım: `breakdown_group_missing` / `breakdown_member_missing` (hesap grubu yok / beklenen üye
   * sette yok) ve `cycle` (formül döngüsü) — kırılım nedenleri K20 raporlanan yedek hücresinde de
   * (`isReported`, status `filled`/`pending_xxx`) taşınır: "toplam başka sette hesaplanıyor" bilgisi.
   */
  failure: z.string().optional(),
  /**
   * Kırılım doğrulama girdisi (spec §3.2 `breakdown_mismatch`): kırılımlı toplamın hücresinde,
   * hesap grubu DIŞINDAKİ her `complete=true` grubun (üyesi sette olan) toplamı/ortalaması —
   * toplama girmez, yalnız denetlenir (AI kontrolü / hücre editörü okur). Yalnız doluyken yazılır;
   * portal görünümünde düşer.
   */
  breakdownChecks: z.array(KpiBreakdownCheckSchema).optional(),
  workflowState: z.enum(KPI_WORKFLOW_STATES).optional(),
  /** C1: girdi `submitted` → türetilmişe yayılır. */
  unverified: z.boolean().optional(),
  isReported: z.boolean().optional(),
  pinned: z.boolean().optional(),
  reportedMismatch: z.number().optional(),
  /** TODO-KPI-23: raporlanan değer XXX (değer bekleniyor) — hesap yapılamıyorsa hücre `pending_xxx`, HESAPLANAMADI değil. */
  reportedPending: z.boolean().optional(),
  source: KpiValueSourceSchema.optional(),
  evidenceCount: z.number().int().optional(),
  valueId: z.string().optional(),
  version: z.number().int().optional(),
  updatedAt: z.string().optional(),
  updatedBy: z.string().nullable().optional(),
  updatedByKind: z.enum(KPI_UPDATED_BY_KINDS).optional(),
  /** danışmanın açıklama isteği (0052) — müşteriye görünür; müşteri yanıtı not iş parçacığında */
  clarificationNote: z.string().nullable().optional(),
  note: z.string().nullable().optional(),
  footnote: z.string().nullable().optional(),
  editable: z.boolean().optional(),
  /**
   * TODO-KPI-15 (DR8) — yalnız portal görünümünde: danışman müşteri girdisini düzelttiyse
   * müşterinin son yazdığı değer (audit `kpi.value.updated` izinden). Sayısal `previousClientValue`,
   * metin `previousClientText`; ikisi de yoksa alan hiç gelmez.
   */
  previousClientValue: z.number().nullable().optional(),
  previousClientText: z.string().nullable().optional(),
  /**
   * M17 (DR8) — yalnız portal: müşterinin son DEĞER yazımından sonraki en yeni danışman değer
   * yazımının zamanı (`kpi_value_versions`, change_kind write|import|reported). Kanıt bağı / taşıma
   * gibi değer dışı sürümler sayılmaz; alan yoksa "danışman düzeltti" gösterilmez.
   */
  correctedAt: z.string().optional(),
});
export type KpiResolvedCell = z.infer<typeof KpiResolvedCellSchema>;

/** entityId → year → hücre. */
const CellMatrixSchema = z.record(
  z.string(),
  z.record(z.coerce.number().int(), KpiResolvedCellSchema),
);
const YearCellsSchema = z.record(z.coerce.number().int(), KpiResolvedCellSchema);

export const KpiResolvedIndicatorSchema = KpiSetIndicatorSchema.extend({
  cells: CellMatrixSchema,
  consolidated: YearCellsSchema.optional(),
  /** Grup işletmesi (parentId) başına ara toplam. */
  subtotals: CellMatrixSchema.optional(),
  /** v2 Y4a — kapalı ama görünür torunu olan düğüm; yalnız yapı (hücre/formül yok). */
  structural: z.literal(true).optional(),
  /** Birim şablonu çözülemedi (ham metin gösterilir). */
  unitUnresolved: z.boolean().optional(),
});
export interface KpiResolvedIndicator extends KpiSetIndicator {
  cells: Record<string, Record<number, KpiResolvedCell>>;
  consolidated?: Record<number, KpiResolvedCell>;
  subtotals?: Record<string, Record<number, KpiResolvedCell>>;
  /** v2 Y4a — yapı satırı (müşteri görünümü); yalnız kimlik/etiket/derinlik taşır. */
  structural?: true;
  unitUnresolved?: boolean;
}

export const KpiGridGeometrySchema = z.object({
  entityTable: z.boolean(),
  consolidatedYears: z.boolean(),
});
export type KpiGridGeometry = z.infer<typeof KpiGridGeometrySchema>;

export const KpiResolvedGridSchema = z.object({
  setId: z.string().uuid(),
  clientId: z.string().uuid(),
  indicators: z.array(KpiResolvedIndicatorSchema),
  entities: z.array(ClientEntitySchema),
  years: z.array(z.number().int()),
  layout: z.enum(KPI_REPORT_TABLE_LAYOUTS),
  geometry: KpiGridGeometrySchema,
  /** M17 T5 — yalnız mali yıl müşterisinde (≠1); takvim müşterisinin grid JSON'u byte-eşit. */
  fiscalYearStartMonth: z.number().int().min(1).max(12).optional(),
  /** v2 §4.3 — yalnız kapsamlı (entityScope verilmiş) çözümde; tam çözümün JSON'u byte-eşit. */
  entityScope: KpiGridEntityScopeSchema.optional(),
});
export interface KpiResolvedGrid {
  setId: string;
  clientId: string;
  indicators: KpiResolvedIndicator[];
  entities: ClientEntity[];
  years: number[];
  layout: KpiReportTableLayout;
  geometry: KpiGridGeometry;
  fiscalYearStartMonth?: number;
  entityScope?: KpiGridEntityScope;
}
